/**
 * BLU-711 A2 — derive the flag-triage cards from a proposal's lines.
 *
 * The server stores flags PER LINE (`mirror` / `drop`, plus a null flag on the
 * blank "not counted" lines). The reviewer works in CARDS, and the two do not
 * map one-to-one:
 *
 *   - a mirror pair is ONE card covering TWO lines (resolving it stamps both),
 *   - a steep drop is one card, one line,
 *   - every blank line collapses into ONE "not counted" card.
 *
 * `flagsCount` on the summary is computed the same way server-side
 * (`summarizeLineRows`), so the "Review N flags" affordance and this list
 * always agree.
 *
 * The uncounted card carries NO lineIds on purpose: blank lines have
 * `flag = null`, so `resolveFlag` rejects them ("This line has no flag to
 * resolve") and the server's no-bypass gate never waits on them. It is an
 * acknowledgement, not a decision, and is tracked in component state.
 */
import type { StocktakeProposalLineRow } from '@threadheads/shared';

export type FlagCardKind = 'mirror' | 'drop' | 'uncounted';

export interface FlagCard {
  /** Stable across refetches (keyed on sku ids, not array position). */
  key: string;
  kind: FlagCardKind;
  /** Lines this card stamps via `resolveFlag`. Empty for `uncounted`. */
  lineIds: string[];
  /** Lines the card renders (all blank lines for `uncounted`). */
  lines: StocktakeProposalLineRow[];
  /** True once every `lineIds` entry carries a `flag_meta.resolved` stamp. */
  resolved: boolean;
}

interface ResolvedStamp {
  at?: string;
  by?: string;
  resolution?: string;
}

/** `flagMeta` is `unknown` on the wire — read it defensively, never cast. */
export function readFlagMeta(
  meta: unknown,
): { pairedSkuId?: string; pairedSku?: string; pct?: number; resolved?: ResolvedStamp } {
  if (!meta || typeof meta !== 'object') return {};
  const record = meta as Record<string, unknown>;
  const resolved =
    record.resolved && typeof record.resolved === 'object'
      ? (record.resolved as ResolvedStamp)
      : undefined;
  return {
    pairedSkuId: typeof record.pairedSkuId === 'string' ? record.pairedSkuId : undefined,
    pairedSku: typeof record.pairedSku === 'string' ? record.pairedSku : undefined,
    pct: typeof record.pct === 'number' ? record.pct : undefined,
    resolved,
  };
}

export function isLineResolved(line: StocktakeProposalLineRow): boolean {
  return readFlagMeta(line.flagMeta).resolved != null;
}

/** How far below the expected quantity a `drop` line landed, as a percentage. */
export function dropPercent(line: StocktakeProposalLineRow): number {
  const pct = readFlagMeta(line.flagMeta).pct;
  if (typeof pct === 'number') return Math.round((1 - pct) * 100);
  if (line.expectedQty <= 0 || line.countedQty == null) return 100;
  return Math.round((1 - line.countedQty / line.expectedQty) * 100);
}

export function buildFlagCards(lines: StocktakeProposalLineRow[]): FlagCard[] {
  const cards: FlagCard[] = [];
  const byId = new Map(lines.map((line) => [line.skuId, line]));

  // --- mirror pairs (one card per pair) ---------------------------------
  const seenMirror = new Set<string>();
  for (const line of lines) {
    if (line.flag !== 'mirror' || seenMirror.has(line.skuId)) continue;
    const partnerId = readFlagMeta(line.flagMeta).pairedSkuId;
    const partner = partnerId ? byId.get(partnerId) : undefined;
    seenMirror.add(line.skuId);
    if (partner) seenMirror.add(partner.skuId);
    const pairLines = partner ? [line, partner] : [line];
    cards.push({
      key: `mirror:${[line.skuId, partnerId ?? ''].sort().join('|')}`,
      kind: 'mirror',
      lineIds: pairLines.map((l) => l.id),
      lines: pairLines,
      resolved: pairLines.every(isLineResolved),
    });
  }

  // --- steep drops ------------------------------------------------------
  for (const line of lines) {
    if (line.flag !== 'drop') continue;
    cards.push({
      key: `drop:${line.skuId}`,
      kind: 'drop',
      lineIds: [line.id],
      lines: [line],
      resolved: isLineResolved(line),
    });
  }

  // --- one card for every blank line ------------------------------------
  const uncounted = lines.filter((line) => line.countedQty == null);
  if (uncounted.length > 0) {
    cards.push({
      key: 'uncounted',
      kind: 'uncounted',
      lineIds: [],
      lines: uncounted,
      resolved: false,
    });
  }

  return cards;
}
